// @flow
import React, { useEffect, useState } from 'react';
import type { Node } from 'react';
import Debug from 'debug';
import Layer from 'ol/layer/Layer';
import { toLonLat } from 'ol/proj';
import mapboxgl from 'mapbox-gl';
import type { Layer as LayerType, Style } from '../types';
import { useMap } from '../context';
import { LayerProvider } from './context';

const debug = Debug('amms:modules:openlayers:layers:vector');

type Props = $ReadOnly<{
    label?: string,
    url: string,
    accessToken?: string,
    style: Style,
    zIndex?: number,
    updateWhileAnimating?: boolean,
    updateWhileInteracting?: boolean,
    children: Node
}>;

function MapboxLayer({ label = '', url, accessToken, style, zIndex, updateWhileAnimating, updateWhileInteracting, children }: Props) {
    const [layer, setLayer] = useState<?LayerType>();
    const [target, setTarget] = useState();
    const map = useMap();

    useEffect(() => {
        let callback;
        if(map) {
            callback = () => {
                setTarget(map.getTarget());
            };
            map.on('change:target', callback);
        }

        return () => {
            if(map) {
                map.un('change:target', callback);
            }
        };
    }, [label, map]);

    useEffect(() => {
        let _layer;

        if(map && target) {
            debug(`Adding Mapbox layer ${label}`);

            const mbMap = new mapboxgl.Map({
                accessToken,
                style: url,
                attributionControl: false,
                boxZoom: false,
                // center: map.getView().getCenter(),
                container: map.getTarget(),
                doubleClickZoom: false,
                dragPan: false,
                dragRotate: false,
                interactive: false,
                keyboard: false,
                pitchWithRotate: false,
                scrollZoom: false,
                touchZoomRotate: false
            });

            _layer = new Layer({
                label,
                zIndex,
                updateWhileAnimating,
                updateWhileInteracting,
                render: function(frameState) {
                    const canvas = mbMap.getCanvas();
                    const viewState = frameState.viewState;

                    const visible = _layer.getVisible();
                    canvas.style.display = visible ? 'block' : 'none';
                    canvas.style.opacity = _layer.getOpacity();

                    // adjust view parameters in mapbox
                    const rotation = viewState.rotation;
                    if (rotation) {
                        mbMap.rotateTo(-rotation * 180 / Math.PI, {
                            animate: false
                        });
                    }
                    mbMap.jumpTo({
                        center: toLonLat(viewState.center),
                        zoom: viewState.zoom - 1,
                        animate: false
                    });

                    // cancel the scheduled update & trigger synchronous redraw
                    // see https://github.com/mapbox/mapbox-gl-js/issues/7893#issue-408992184
                    // NOTE: THIS MIGHT BREAK WHEN UPDATING MAPBOX
                    if (mbMap._frame) {
                        mbMap._frame.cancel();
                        mbMap._frame = null;
                    }
                    mbMap._render();

                    return canvas;
                }
            });

            map.addLayer(_layer);
            setLayer(_layer);
        }

        return () => {
            if(map && _layer) {
                debug(`Removing Mapbox layer ${label}`);

                map.removeLayer(_layer);
                setLayer(null);
            }
        };
    }, [map, target, label, url, accessToken, zIndex, updateWhileAnimating, updateWhileInteracting]);

    useEffect(() => {
        if(layer) {
            if(style) {
                debug(`Adding style ${label ? `to ${label}` : ''}: `, style);

                let _style = style;
                if(typeof style === 'function') {
                    const defaultStyleFunction = layer.getStyleFunction();
                    _style = (feature, resolution) => style(feature, resolution, defaultStyleFunction);
                }

                layer.setStyle(_style);
            }
        }
    }, [label, layer, style]);

    return (
        <LayerProvider layer={layer}>
            {children}
        </LayerProvider>
    );
}

export default MapboxLayer;